import { useApiQuery } from '../useApiQuery';
import { taskKeys } from '@/lib/queryKeys';
import { taskEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import type { Task, TaskListParams, TaskListResponse } from '@/types/task';

export function useTasks(
  params: TaskListParams = {},
  options?: { enabled?: boolean; fetchAll?: boolean },
) {
  return useApiQuery<TaskListResponse>({
    queryKey: taskKeys.list({ ...params, fetchAll: options?.fetchAll }),
    queryFn: async () => {
      const { sortOrder, ...rest } = params;
      const requestParams = { ...rest, sortDir: sortOrder };

      if (!options?.fetchAll) {
        const res = await apiGet<{ data: Task[]; total: number; page: number; limit: number }>(
          operationsClient,
          taskEndpoints.list(),
          { params: requestParams },
        );
        return { tasks: res.data, total: res.total, page: res.page, limit: res.limit };
      }

      const limit = Number(params.limit ?? 100);
      const pageSize = Number.isFinite(limit) && limit > 0 ? limit : 100;
      let page = Number(params.page ?? 1);
      const tasks: Task[] = [];
      let total = 0;

      for (;;) {
        const res = await apiGet<{ data: Task[]; total: number; page: number; limit: number }>(
          operationsClient,
          taskEndpoints.list(),
          { params: { ...requestParams, page, limit: pageSize } },
        );
        tasks.push(...res.data);
        total = res.total;
        const returnedLimit = res.limit || pageSize;
        const returnedPage = res.page || page;
        const hasKnownTotal = total > 0;
        if (
          (hasKnownTotal && tasks.length >= total) ||
          res.data.length === 0 ||
          res.data.length < returnedLimit
        ) {
          return { tasks, total, page: 1, limit: tasks.length || returnedLimit };
        }
        page = returnedPage + 1;
      }
    },
    enabled: options?.enabled,
    showErrorToast: false,
  });
}
